import * as d3 from 'd3';
import { PointerEvent as ReactPointerEvent } from 'react';

export type Vector2 = {
  readonly x: number;
  readonly y: number;
  add: (value: Vector2Alike) => Vector2;
  subtract: (value: Vector2Alike) => Vector2;
  negate: () => Vector2;
  distance: (to: Vector2Alike) => number;
  divide: (by: Vector2Alike) => Vector2;
  multiply: (by: Vector2Alike) => Vector2;
  round: () => Vector2;
  floor: () => Vector2;
  max: (value: Vector2Alike) => Vector2;
  clamp: (min: Vector2, max: Vector2) => Vector2;
  parseToString: () => string;
};

export type Vector2Alike = { x: number; y: number };

// For easier comparison between different Vector2 instances, we cache result of the vector2 function.
// Vector2 is also immutable so it make sense.
const cachedVectors2 = new Map<string, Vector2>();

export const vector2 = (x = 0, y = 0): Vector2 => {
  const cacheKey = `${x}:${y}`;
  const cachedVector2 = cachedVectors2.get(cacheKey);

  if (cachedVector2) {
    return cachedVector2;
  }

  const add: Vector2['add'] = (value) => vector2(x + value.x, y + value.y);
  const subtract: Vector2['subtract'] = (value) => vector2(x - value.x, y - value.y);
  const negate: Vector2['negate'] = () => vector2(-x, -y);
  const distance: Vector2['distance'] = (to) => Math.hypot(to.x - x, to.y - y);
  const divide: Vector2['divide'] = (by) => vector2(x / by.x, y / by.y);
  const multiply: Vector2['multiply'] = (by) => vector2(x * by.x, y * by.y);
  const round: Vector2['round'] = () => vector2(Math.round(x), Math.round(y));
  const floor: Vector2['floor'] = () => vector2(Math.floor(x), Math.floor(y));
  const max: Vector2['max'] = (value) => vector2(Math.max(x, value.x), Math.max(y, value.y));
  const clamp: Vector2['clamp'] = (min, max) =>
    vector2(Math.min(Math.max(x, min.x), max.x), Math.min(Math.max(y, min.y), max.y));
  const parseToString: Vector2['toString'] = () => `x=${x};y=${y}`;

  const resultVector2: Vector2 = {
    x,
    y,
    add,
    subtract,
    negate,
    distance,
    divide,
    multiply,
    round,
    floor,
    max,
    clamp,
    parseToString,
  };

  cachedVectors2.set(cacheKey, resultVector2);

  return resultVector2;
};

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace Vector2 {
  export const zero = vector2(0, 0);

  export const fromRectOrigin = (rect: DOMRect): Vector2 => vector2(rect.left, rect.top);

  export const fromPointerEvent = (
    event: PointerEvent | ReactPointerEvent,
    use: 'screen' | 'page' = 'screen',
  ): Vector2 => {
    switch (use) {
      case 'screen':
        return vector2(event.screenX, event.screenY);
      case 'page':
        return vector2(event.pageX, event.pageY);
    }
  };

  export const linearInterpolation = (from: Vector2, to: Vector2, alpha: number): Vector2 => {
    const interpolateX = d3.interpolate(from.x, to.x);
    const interpolateY = d3.interpolate(from.y, to.y);

    return vector2(interpolateX(alpha), interpolateY(alpha));
  };
}
